import React, { useEffect, useState } from 'react';
import { Loader2, PlayCircle, Quote, Trophy, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { QuizRunner } from './QuizRunner';

export const mmHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const Lesson = ({ week, lesson, best, onScored }) => {
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => { setResult(null); }, [lesson.day, week]);

  const submit = async (answers) => {
    try {
      const r = await api.post(`/academy/money-moves/lesson/${week}/${lesson.day}/submit`, { answers }, { headers: mmHeaders() });
      setResult(r.data); onScored(`${week}-${lesson.day}`, r.data.best);
      toast.success(`Worksheet graded: ${r.data.score}%`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
  };

  return (
    <div className="space-y-6" data-testid={`mm-lesson-${week}-${lesson.day}`}>
      <div>
        <div className="text-[10px] font-black tracking-[0.3em] uppercase text-emerald-400">Day {lesson.day} · {lesson.standard}</div>
        <h3 className="text-2xl font-black text-white mt-1">{lesson.title}</h3>
        {lesson.hook && <p className="text-zinc-300 text-sm mt-2 italic">{lesson.hook}</p>}
      </div>
      {lesson.video?.embed_url ? (
        <div className="aspect-video border-2 border-zinc-800" data-testid="mm-lesson-video">
          <iframe src={lesson.video.embed_url} title={lesson.video.title || lesson.title} className="w-full h-full" allowFullScreen allow="accelerometer; encrypted-media; gyroscope; picture-in-picture" />
        </div>
      ) : (
        <div className="border-2 border-dashed border-zinc-800 p-4 text-xs text-zinc-500 flex items-center gap-2" data-testid="mm-lesson-video-missing"><PlayCircle className="w-4 h-4" /> Video coming soon for this lesson.</div>
      )}
      <div className="space-y-3 text-sm text-zinc-200 leading-relaxed">{lesson.reading.map((p, i) => <p key={i}>{p}</p>)}</div>
      <div className="border-l-4 border-yellow-500 bg-yellow-500/5 p-4" data-testid="mm-lesson-tanstaafl">
        <div className="text-[10px] font-black tracking-[0.3em] uppercase text-yellow-400 mb-1">TANSTAAFL</div>
        <p className="text-sm text-zinc-100">{lesson.tanstaafl}</p>
      </div>
      {lesson.sports_example?.title && (
        <div className="border-2 border-zinc-800 p-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-white"><Trophy className="w-4 h-4 text-emerald-400" /> {lesson.sports_example.title}</div>
          <p className="text-sm text-zinc-300 mt-2">{lesson.sports_example.body}</p>
        </div>
      )}
      {lesson.key_terms?.length > 0 && (
        <div className="flex flex-wrap gap-2">{lesson.key_terms.map((t) => <span key={t} className="text-[11px] border border-zinc-700 px-2.5 py-1 text-zinc-300">{t}</span>)}</div>
      )}
      {lesson.coach_b_says && (
        <div className="flex gap-3 items-start"><Quote className="w-5 h-5 text-emerald-400 shrink-0" /><p className="text-base font-black text-white">"{lesson.coach_b_says}" <span className="text-zinc-500 text-xs font-bold">— Coach B</span></p></div>
      )}
      <div className="border-t-2 border-zinc-800 pt-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-black uppercase tracking-[0.25em] text-white">Lesson Worksheet</div>
          {best !== undefined && best !== null && <div className="text-[11px] text-zinc-400" data-testid="mm-lesson-best">Best: <b className="text-emerald-400">{best}%</b></div>}
        </div>
        <QuizRunner key={`${week}-${lesson.day}-${attempt}`} questions={lesson.worksheet} onSubmit={submit} result={result} submitLabel="Submit Worksheet" testidPrefix="mm-ws" />
        {result && (
          <button onClick={() => { setResult(null); setAttempt((a) => a + 1); }} className="mt-4 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="mm-ws-retry"><RotateCcw className="w-3.5 h-3.5" /> Try again (best score counts)</button>
        )}
      </div>
    </div>
  );
};

export const WeekView = ({ week, progress, onScored }) => {
  const [data, setData] = useState(null);
  const [day, setDay] = useState(1);

  useEffect(() => {
    let t; let alive = true;
    setData(null); setDay(1);
    const load = () => api.get(`/academy/money-moves/week/${week}`, { headers: mmHeaders() }).then((r) => {
      if (!alive) return;
      setData(r.data);
      if (r.data.status === 'generating') t = setTimeout(load, 6000);
    }).catch((e) => alive && setData({ status: 'error', detail: e?.response?.data?.detail }));
    load();
    return () => { alive = false; clearTimeout(t); };
  }, [week]);

  if (!data || data.status === 'generating') {
    return <div className="py-16 text-center" data-testid="mm-week-generating"><Loader2 className="w-7 h-7 animate-spin text-emerald-400 mx-auto" /><p className="text-sm text-zinc-400 mt-3">Coach B is preparing this week's lessons… (about a minute the first time)</p></div>;
  }
  if (data.status === 'error') return <div className="text-sm text-red-400 py-8" data-testid="mm-week-error">{data.detail || 'Could not load this week.'}</div>;
  const lesson = data.lessons.find((l) => l.day === day) || data.lessons[0];
  return (
    <div data-testid={`mm-week-${week}`}>
      <div className="flex flex-wrap gap-1.5 mb-6">
        {data.lessons.map((l) => {
          const s = progress[`${week}-${l.day}`];
          return (
            <button key={l.day} onClick={() => setDay(l.day)} className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest border-2 transition-colors ${day === l.day ? 'bg-emerald-500 border-emerald-500 text-black' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'}`} data-testid={`mm-day-${l.day}`}>
              Day {l.day}{s !== undefined && s !== null ? ` · ${s}%` : ''}
            </button>
          );
        })}
      </div>
      <Lesson week={week} lesson={lesson} best={progress[`${week}-${lesson.day}`]} onScored={onScored} />
    </div>
  );
};
